'use client';

import { useHero } from './HeroContext';
import { useRef, useEffect, useState } from 'react';
import useDictionary from '@modules/Dictionary/useDictionary';
import prefersReducedMotion from '@utils/prefersReducedMotion';
import prefersReducedData from '@utils/prefersReducedData';

const MOBILE_MQ = '(max-width: 719px)';
const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';

const getImageSrc = (img) => (typeof img === 'string' ? img : img?.sourceUrl || img?.src || img?.url || '');

/**
 * @param {{ video: string, videoMobile?: string }} props
 */
export default function HeroVideo({ video, videoMobile }) {
  const { video: contextVideo, image } = useHero();
  const activeVideo = contextVideo || video;
  const heroMediaRef = /** @type {{ current: HTMLDivElement | null }} */ (useRef(null));
  const videoRef = /** @type {{ current: HTMLVideoElement | null }} */ (useRef(null));
  const pausedRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [reducedData, setReducedData] = useState(false);
  const { dictionary } = useDictionary();

  // WCAG 2.2.2: the video can be paused, and starts paused when reduced motion is preferred
  useEffect(() => {
    setReducedData(prefersReducedData());
    if (!window.matchMedia(REDUCED_MOTION_MQ).matches) return;
    pausedRef.current = true;
    videoRef.current?.pause();
    setPaused(true);
  }, []);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    pausedRef.current = !pausedRef.current;
    if (pausedRef.current) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
    setPaused(pausedRef.current);
  };

  useEffect(() => {
    /** @type {number} */
    let frameId;
    const handleScroll = () => {
      if (prefersReducedMotion()) return;
      frameId = requestAnimationFrame(() => {
        if (heroMediaRef.current) {
          heroMediaRef.current.style.transform = `translateY(${window.scrollY * 0.5}px)`;
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    const mq = window.matchMedia(MOBILE_MQ);

    const update = () => {
      if (!videoRef.current) return;
      // Data saver: skip the video file and show the hero image as poster instead
      if (prefersReducedData()) {
        videoRef.current.preload = 'none';
        videoRef.current.poster = getImageSrc(image);
        return;
      }
      const src = videoMobile && mq.matches ? videoMobile : activeVideo;
      videoRef.current.src = src;
      videoRef.current.autoplay = !pausedRef.current;
      videoRef.current.load();
    };

    update();

    if (videoMobile) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
  }, [activeVideo, videoMobile]);

  if (!activeVideo) {
    return <div className="Hero__video Hero__video--empty"></div>;
  }

  return (
    <>
      <div className="Hero__video" ref={heroMediaRef}>
        <video autoPlay muted loop playsInline preload="auto" ref={videoRef} />
      </div>
      {reducedData ? null : (
        <button type="button" className="Hero__video-toggle" onClick={togglePlayback}>
          {paused ? dictionary?.hero?.playVideo || 'Play video' : dictionary?.hero?.pauseVideo || 'Pause video'}
        </button>
      )}
    </>
  );
}
